import { DocumentJobStatus, UploadDocumentJob } from '@domain/types';

import {
  type BatchEndMode,
  batchStartDateFromJobs,
  groupJobsByBatch,
  trackBatchUploadEnd,
} from './uploadAnalytics';

interface BatchEntry {
  jobs: Map<string, UploadDocumentJob>;
  documentIdToJobId: Map<string, string>;
  expectedCount: number;
  startDate: Date;
}

const pendingBatches = new Map<string, BatchEntry>();
const completedJobKeys = new Set<string>();
/**
 * Documents that reached a terminal status before their batch was registered.
 * Drained inside registerFinishedBatchJobs so the event is never lost.
 */
const earlyTerminals = new Set<string>();

/**
 * True while a multi-file sending batch is awaiting analysis completion.
 * Prevents earlyTerminals from growing on single-file uploads or when the
 * defer feature is inactive.
 */
let deferredBatchEndTrackingActive = false;

function jobCompletionKey(batchUploadId: string, jobId: string): string {
  return `${batchUploadId}:${jobId}`;
}

function deactivateTrackingIfIdle(): void {
  if (pendingBatches.size === 0 && completedJobKeys.size === 0 && earlyTerminals.size === 0) {
    deferredBatchEndTrackingActive = false;
  }
}

/** The batch's total file count as declared on its jobs, if any job carries it. */
function declaredBatchCount(jobs: UploadDocumentJob[]): number | undefined {
  return jobs.find((job) => job.batchDocumentsCount != null)?.batchDocumentsCount;
}

function mergeJobsIntoBatch(batchUploadId: string, jobs: UploadDocumentJob[]): BatchEntry {
  let entry = pendingBatches.get(batchUploadId);

  if (!entry) {
    entry = {
      jobs: new Map(),
      documentIdToJobId: new Map(),
      expectedCount: 0,
      startDate: batchStartDateFromJobs(jobs),
    };
    pendingBatches.set(batchUploadId, entry);
  } else {
    entry.startDate = batchStartDateFromJobs([...entry.jobs.values(), ...jobs]);
  }

  for (const job of jobs) {
    entry.jobs.set(job.id, job);
    if (job.documentId) {
      entry.documentIdToJobId.set(job.documentId, job.id);
    }
  }

  // Prefer the declared total; otherwise fall back to the jobs seen so far.
  // expectedCount only grows, so a later drain can never shrink it below a
  // previously declared total.
  entry.expectedCount = declaredBatchCount(jobs) ?? Math.max(entry.expectedCount, entry.jobs.size);

  return entry;
}

function cleanupBatch(batchUploadId: string, entry: BatchEntry): void {
  pendingBatches.delete(batchUploadId);
  for (const jobId of entry.jobs.keys()) {
    completedJobKeys.delete(jobCompletionKey(batchUploadId, jobId));
  }
  deactivateTrackingIfIdle();
}

function tryEmitBatchEnd(batchUploadId: string, entry: BatchEntry): void {
  const completedCount = [...entry.jobs.keys()].filter((id) =>
    completedJobKeys.has(jobCompletionKey(batchUploadId, id)),
  ).length;

  if (completedCount < entry.expectedCount) return;

  if (entry.expectedCount <= 1) {
    cleanupBatch(batchUploadId, entry);
    return;
  }

  trackBatchUploadEnd({
    batchUploadId,
    documentsCount: entry.expectedCount,
    startDate: entry.startDate,
  });

  cleanupBatch(batchUploadId, entry);
}

function markJobComplete(batchUploadId: string, jobId: string): void {
  const key = jobCompletionKey(batchUploadId, jobId);
  if (completedJobKeys.has(key)) return;
  completedJobKeys.add(key);

  const entry = pendingBatches.get(batchUploadId);
  if (!entry) return;

  tryEmitBatchEnd(batchUploadId, entry);
}

/**
 * Activates deferred batch-end tracking for a multi-file sending upload session.
 * Call when a batch with more than one file starts (before analysis may complete).
 */
export function onMultiFileBatchUploadStarted(): void {
  deferredBatchEndTrackingActive = true;
}

/**
 * Registers finished upload jobs and emits `Batch File Upload End` once the
 * batch's expected file count has completed. Upload failures always count
 * immediately. In `immediate` mode successful jobs count right away; in
 * `awaitAnalysis` mode they wait for `notifyRootDocumentTerminal`. Documents
 * that reached a terminal status before this call are drained from the
 * earlyTerminals buffer so the event is never lost.
 */
function registerFinishedBatchJobs(finishedJobs: UploadDocumentJob[], mode: BatchEndMode): void {
  const jobsByBatch = groupJobsByBatch(finishedJobs);
  if (jobsByBatch.size === 0) return;

  deferredBatchEndTrackingActive = true;
  const countSuccessImmediately = mode === 'immediate';

  for (const [batchUploadId, jobs] of jobsByBatch) {
    const entry = mergeJobsIntoBatch(batchUploadId, jobs);

    for (const job of jobs) {
      if (countSuccessImmediately || job.status === DocumentJobStatus.Failed) {
        markJobComplete(batchUploadId, job.id);
      }
    }

    for (const docId of earlyTerminals) {
      const jobId = entry.documentIdToJobId.get(docId);
      if (jobId) {
        earlyTerminals.delete(docId);
        markJobComplete(batchUploadId, jobId);
      }
    }
  }
}

/** Called when a root import document reaches a terminal analysis/upload status. */
export function notifyRootDocumentTerminal(documentId: string): void {
  if (!deferredBatchEndTrackingActive) return;

  for (const [batchUploadId, entry] of pendingBatches) {
    const jobId = entry.documentIdToJobId.get(documentId);
    if (jobId) {
      markJobComplete(batchUploadId, jobId);
      return;
    }
  }
  // Batch not registered yet — buffer the id so register() can pick it up.
  earlyTerminals.add(documentId);
}

export function onUploadQueueDrained(finishedJobs: UploadDocumentJob[], mode: BatchEndMode): void {
  registerFinishedBatchJobs(finishedJobs, mode);
}

/** Clears all pending batch state. Call when the user dismisses the upload panel. */
export function clearBatchUploadAnalysisState(): void {
  pendingBatches.clear();
  completedJobKeys.clear();
  earlyTerminals.clear();
  deferredBatchEndTrackingActive = false;
}

/** @internal Test helper */
export function resetBatchUploadAnalysisTracker(): void {
  clearBatchUploadAnalysisState();
}

/** @internal Test helper */
export function isDeferredBatchEndTrackingActive(): boolean {
  return deferredBatchEndTrackingActive;
}
